import { getCoverage, type BaselinePolicy } from '../../lib/api'
import { describeFailure, useApi } from './useApi'
import './live.css'

/**
 * What each status means, in words a customer can act on.
 *
 * The database holds a one-word status and a written sentence per policy. The sentence is the
 * useful half and it comes from the server; these are only the headings the sentences sit under.
 *
 * `legend` is the same fact in a phrase short enough to sit beside a swatch, and `heading` is what
 * the expanded list uses. Two lengths of the same statement rather than two statements — if they
 * ever disagree, one of them is wrong.
 *
 * An unrecognised status falls through to itself rather than to a friendly guess — the same rule
 * the freshness card follows for scan states. A status this build has no heading for is a policy
 * nobody here has decided how to describe, and it must appear looking unfamiliar rather than be
 * dropped: the headline denominator counts every row, so a group silently omitted would leave the
 * groups summing to less than the number above them.
 */
const REASON: Record<string, { heading: string; legend: string; note: string }> = {
  // PLAIN WORDS (owner, 2026-09-25: "gibberish ... the customer will not understand"). These used to
  // talk about Graph versions and API objects. The facts are unchanged; the reader is a customer.
  //
  // "We have found no way", not "Microsoft does not let apps": the Terms (28 September 2026) say
  // "we have found no way for applications to read the setting on Microsoft Graph v1.0, the version
  // we use", which is what we know. That Microsoft offers no way at all is a claim we cannot back.
  unavailable: {
    heading: 'We have found no way for apps to read these settings',
    legend: 'settings we have found no way for apps to read',
    note:
      'We have found no way for applications to read these settings on Microsoft Graph v1.0, the ' +
      'version of Microsoft’s interface we use, so we cannot check them. Check them yourself in ' +
      'the Microsoft admin centre.',
  },
  // A gap that is ours alone (0171): Microsoft lets apps read the setting and nothing stops us
  // checking it, we just have not written the check. It used to sit under "unavailable", which
  // blamed Microsoft for it. MS.AAD.5.2v1, the one policy 0171 filed here, has its check since
  // 0172, so no policy carries this today; the words stay for the next gap that is ours alone.
  not_built: {
    heading: 'We do not check these yet',
    legend: 'Microsoft lets apps read the setting, but we do not check it yet',
    note:
      'Microsoft lets apps read these settings, but we do not check them yet. Until we do, check ' +
      'them yourself in the Microsoft admin centre.',
  },
  permission: {
    heading: 'Would need an extra Microsoft permission',
    legend: 'would need an extra permission we do not ask for',
    note:
      'We could check this, but only with an extra permission that every customer would have to ' +
      'approve. We have not asked for it.',
  },
  // Not "it cannot be read from your tenant": the Terms (28 September 2026) say we do not ask for
  // the Azure access needed to read it, which is what we know. Both roles we ask for include an
  // Azure Service Health right, so "cannot" was not ours to claim.
  external: {
    heading: 'Not a Microsoft 365 setting',
    legend: 'not a Microsoft 365 setting',
    note:
      'This is set up outside Microsoft 365, and we do not ask for the access needed to read it ' +
      'there.',
  },
  licence: {
    heading: 'Checked if you have the licence',

    // Not "need Entra ID P2" in the legend. Today every one of them needs Privileged Identity
    // Management, which comes with Microsoft Entra ID P2 or Microsoft Entra ID Governance
    // (TenantLicensing counts either); the note under each control names the licence, and a legend
    // that hard-coded one would be quietly wrong the first time a control needs a different one.
    legend: 'checked if you have the licence they need',
    note:
      'We check these when your tenant has the licence they need. Without it they are marked ' +
      'not applicable — you cannot fail a control you would have to buy.',
  },
  // "Where Microsoft allows the read" (Terms, 28 September 2026): nine of these read Conditional
  // Access, which Microsoft refuses without Microsoft Entra ID P1 or P2, and then the check reports
  // that it could not run or that the policy is not applicable -- never a pass. The note says which
  // ones by what they read rather than by a count, so it cannot go stale when a check is added.
  assessed: {
    heading: 'Checked on every assessment, where Microsoft allows the read',
    legend: 'checked on every assessment, where Microsoft allows the read',
    note:
      'We check each of these every time we assess your tenant. Those that read your Conditional ' +
      'Access policies need Microsoft Entra ID P1 or P2: in a tenant without either, where ' +
      'Microsoft refuses that read, they report that they could not run or that the policy is not ' +
      'applicable, unless Security Defaults settles the question. Neither is counted as a pass.',
  },
}

/**
 * The licence the six Privileged Identity Management controls need, as TenantLicensing reads it:
 * the Entra ID P2 service plan or the Entra ID Governance one, either of which carries PIM. It said
 * "Needs Microsoft Entra ID P2", which a tenant licensed through Governance would read as not theirs.
 */
const PIM_LICENCE =
  'Needs Privileged Identity Management, which comes with Microsoft Entra ID P2 or Microsoft Entra ' +
  'ID Governance.'

/**
 * The reason under each control we cannot fully check, in plain words.
 *
 * The database keeps the technical explanation (scuba_policy.detail) -- it is the audit record, it is
 * part of the coverage fingerprint every scan and sealed report is stamped with, and rewriting it
 * would make every existing report say its coverage "changed since this assessment". So the screen
 * says the same fact plainly here, keyed by the CISA reference. A control with no entry shows only
 * its group's note, never the technical text: that was the jargon this replaced.
 *
 * The two say the same fact, so when one is wrong both are: 0171 corrected the database's sentence
 * for MS.AAD.5.2v1 along with this one, and accepted the "changed since this assessment" that
 * costs, because the old sentence was not true. 0172 wrote the check for MS.AAD.5.2v1, so it is
 * assessed, has no reason in either place, and its entry here is gone.
 */
const PLAIN_DETAIL: Record<string, string> = {
  // Not "we can check it only if you also give us read access to Azure": there is no check for it
  // (Terms: one of the four "not implemented at all"), so read access to Azure would check nothing.
  'MS.AAD.4.1v1':
    'Where your security logs are sent is set up in Azure, not in Microsoft 365, and we do not ask ' +
    'for the Azure access needed to read it, so we have no check for it.',
  'MS.AAD.7.4v1': PIM_LICENCE,
  'MS.AAD.7.5v1': PIM_LICENCE,
  'MS.AAD.7.6v1': PIM_LICENCE,
  'MS.AAD.7.7v1': PIM_LICENCE,
  'MS.AAD.7.8v1': PIM_LICENCE,
  'MS.AAD.7.9v1': PIM_LICENCE,
  'MS.AAD.9.1v1':
    'Checking this needs an extra Microsoft permission, to read risky app identities, that we do ' +
    'not currently ask for.',
  // The Terms' words (28 September 2026): "we have found no way for applications to read the
  // setting on Microsoft Graph v1.0". It said Microsoft does not let apps read it, which we cannot
  // back.
  'MS.AAD.2.2v1':
    'We have found no way for applications to read this setting, who receives these alerts, on ' +
    'Microsoft Graph v1.0, the version of Microsoft’s interface we use. We do check that ' +
    'high-risk users and sign-ins are blocked.',
  'MS.AAD.8.3v1':
    'We have found no way for applications to read this setting, the list of allowed guest ' +
    'domains, on Microsoft Graph v1.0, the version of Microsoft’s interface we use.',
}

/** CISA's own words for how binding a control is, as a reader would say them. */
function criticalityLabel(value: string): string {
  return value === 'SHALL' ? 'Required' : value === 'SHOULD' ? 'Recommended' : value
}

/** The inventory's area names, as things rather than departments. */
const AREA_PHRASE: Record<string, string> = {
  Endpoint: 'devices',
  Identity: 'identity',
  Email: 'email',
  Data: 'data sharing',
}

/** "a, b and c". */
function listPhrase(items: string[]): string {
  if (items.length <= 1) return items.join('')
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

/**
 * The ledger, left to right: what we always look at, what we look at conditionally, then the gaps.
 *
 * This is the BAR's order, and the legend follows the bar because a key that does not run in the
 * same order as the thing it explains is a key somebody has to decode. The expanded list below
 * keeps the opposite order — see GROUP_ORDER.
 *
 * Among the gaps, the one that is ours alone (not_built) comes first here and first in the list
 * below: it sits beside what we check because it is the nearest to being checked, and it leads the
 * reading list because it is the gap nobody else is to blame for.
 */
const LEDGER_ORDER = ['assessed', 'licence', 'not_built', 'unavailable', 'permission', 'external']

/**
 * Gaps first, then the conditional ones, then what we do assess.
 *
 * Deliberately the opposite of flattering, and deliberately the opposite of the bar above it. A
 * buyer who knows this baseline has thirty-four policies will count ours, and raising the gap
 * before they find it is the difference between credibility and a caught overstatement — the
 * argument migration 0079 makes for storing the whole baseline in the first place. The bar is a
 * picture of a ratio and reads best in ratio order; this is the reading list, and it leads with
 * what we cannot do.
 */
const GROUP_ORDER = ['not_built', 'unavailable', 'permission', 'external', 'licence', 'assessed']

function rankOf(status: string, order: string[]): number {
  const rank = order.indexOf(status)
  return rank < 0 ? order.length : rank
}

/**
 * What to call the baseline, from what actually came back.
 *
 * "Entra ID" only while every policy is one. The table admits any SCuBA product id — MS.EXO,
 * MS.SHAREPOINT and the rest are the same shape — so the day a second baseline lands, a heading
 * hard-coded to Entra ID would be naming a scope it no longer describes, in the one card on this
 * dashboard whose entire subject is scope.
 */
function baselineName(policies: BaselinePolicy[]): string {
  return policies.length > 0 && policies.every((p) => p.reference.startsWith('MS.AAD.'))
    ? 'CISA SCuBA Entra ID'
    : 'CISA SCuBA'
}

/**
 * A SCuBA product code as a reader knows it -- "EXO" and "DEFENDER" are CISA's baseline file names,
 * not what anyone calls the product. An unknown code shows as it is.
 */
const PRODUCT_NAME: Record<string, string> = {
  AAD: 'Microsoft Entra ID',
  EXO: 'Exchange Online',
  DEFENDER: 'Microsoft Defender for Office 365',
  SHAREPOINT: 'SharePoint Online',
  TEAMS: 'Microsoft Teams',
  POWERPLATFORM: 'Power Platform',
  POWERBI: 'Power BI',
}

function productName(code: string): string {
  return PRODUCT_NAME[code.toUpperCase()] ?? code
}

/**
 * One status group, as the bar and the legend see it.
 *
 * The tone comes from `hasCheck` — the SERVER's rule, carried per policy — and not from the status
 * word, so a status this build has never heard of still lands on the correct side of the ledger. A
 * group whose policies disagree with each other falls to "no check", which understates our
 * coverage; that is the only direction this product is allowed to be wrong in.
 */
function toneOf(status: string, policies: BaselinePolicy[]): string {
  if (policies.length > 0 && policies.every((p) => p.hasCheck)) {
    return status === 'assessed' ? 'is-always' : 'is-conditional'
  }

  return 'is-none'
}

/**
 * The coverage ledger: every policy in the published baseline, and whether we look at it.
 *
 * THIS IS THE HEADLINE THAT REPLACES A SECURITY SCORE, and the reason there is no score is worth
 * having beside the code rather than only in docs/launch-plan.md. Nearly all this product's
 * evidence is Entra ID. A single number out of 100 would imply whole-tenant coverage it does not
 * have, and it would be the most misleading thing on the page precisely because it is the easiest
 * thing to screenshot. So: no score, no grade, no compliance percentage — and no percentage of our
 * own at all, because a percentage of a baseline is one step from a grade and reads as one. (The
 * Microsoft Secure Score gauge on the dashboard is Microsoft's number, shown as Microsoft's, in its
 * own panel -- never this card's, and never counted into it.)
 *
 * What is here instead is a ratio with a published denominator. Thirty-four is CISA's number, not
 * ours; a customer can count it against the document. A ratio like that cannot flatter, which is
 * the whole reason it is the claim the product sells on.
 *
 * Every number below is derived from the rows the server returned. None is written into this file
 * — a hard-coded "29 of 34" would keep saying 29 after the baseline moved, which is the specific
 * way a coverage claim goes stale without anybody noticing.
 */
/** "17 September 2026" from an ISO date, read as the calendar day it names. */
function plainDate(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T12:00:00Z`)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { dateStyle: 'long', timeZone: 'UTC' })
}

export function CoverageLedger() {
  // No polling. The baseline is a published document; it does not change while somebody watches
  // this card, and a poll here would ask a question whose answer is the same every time.
  const coverage = useApi(() => getCoverage(), [])

  if (coverage.state === 'loading') {
    return (
      <section className="dash-panel">
        <div className="dash-kicker">Baseline coverage</div>
        <p className="live-empty">Loading…</p>
      </section>
    )
  }

  if (coverage.state === 'error') {
    // The panel shell stays, with the failure inside it. Rendering nothing would remove the one
    // statement on this page that says how much of the baseline the numbers below cover, and the
    // dashboard would quietly go back to implying it covers all of it.
    return (
      <section className="dash-panel">
        <div className="dash-kicker">Baseline coverage</div>
        <p className="live-error">{describeFailure(coverage.status)}</p>
      </section>
    )
  }

  const { total, withCheck, byStatus, policies, baseline, checkInventory } = coverage.data

  // Built from what arrived, not from a list this file maintains, so nothing can be dropped by
  // having no entry in one. Order is applied afterwards; an unknown status sorts to the end of
  // both orders rather than out of either.
  const groups = Object.entries(byStatus).map(([status, count]) => {
    const own = policies.filter((p) => p.status === status)
    return { status, count, known: status in REASON, policies: own, tone: toneOf(status, own) }
  })

  const ledger = [...groups].sort(
    (a, b) => rankOf(a.status, LEDGER_ORDER) - rankOf(b.status, LEDGER_ORDER))

  const detail = [...groups].sort(
    (a, b) => rankOf(a.status, GROUP_ORDER) - rankOf(b.status, GROUP_ORDER))

  return (
    <section className="dash-panel coverage">
      <div className="dash-kicker">Baseline coverage</div>

      {/*
        "have a check", not "assessed". Some of these run only where the tenant holds the licence
        they need, so calling all of them assessed would be wrong by that many on any tenant
        without it -- an overstatement of exactly the kind this card exists to prevent, in the
        sentence meant to prevent it.
      */}
      <p className="dash-figure">
        <span className="dash-figure-value">{withCheck}</span>
        <span className="dash-figure-unit">of {total}</span>
      </p>

      <p className="dash-lede">
        {baselineName(policies)} controls that Defenssive can check. There is no score out of 100
        on purpose: one number would suggest we had looked at everything. Checks on devices, email
        and data are in your findings, not in this count.
      </p>

      {/*
        The version the denominator is measured against, driven by the PRODUCTS actually shown rather
        than by whatever versions came back — so a product on the card with no version cannot slip
        through undated. For each product present in the ledger:
          · a reconciled version → its provenance line, linked to the exact source ref;
          · a version whose count has drifted from the ledger → a stale-provenance caution;
          · no version at all → a missing-provenance caution.
        An older server that sends no baseline field at all renders nothing here — better no date than
        a warning invented from an absent field. A NEW server that sends an empty list is different:
        its products genuinely have no version, and that is the caution above, not silence.
      */}
      {baseline !== undefined &&
        [...new Set(policies.map((p) => p.reference.split('.')[1]))].map((product) => {
          const v = baseline.find((b) => b.product === product)

          if (!v) {
            return (
              <p key={product} className="coverage-provenance coverage-provenance-warn">
                The {product} baseline is shown without a version — its provenance is missing. Treat
                the {total} above as undated until it is restored.
              </p>
            )
          }

          if (!v.reconciled) {
            // Name the counts only when they differ. A policy renamed or reworded in place moves the
            // fingerprint without moving the count, so "pinned at 34, holds 34" would read as a
            // contradiction; there the sentence stands on "no longer matches" alone.
            const countClause =
              v.expectedPolicyCount !== v.ledgerPolicyCount
                ? ` (that revision had ${v.expectedPolicyCount} policies; the list now holds ${v.ledgerPolicyCount})`
                : ''

            return (
              <p key={product} className="coverage-provenance coverage-provenance-warn">
                {/* The revision id is kept for an auditor, on the link, rather than printed as a
                    12-character hash in the middle of a sentence. */}
                The {v.name} version label is out of date: the baseline revision it names no longer
                matches the current baseline{countClause}. The count above is current; its version
                label is not.{' '}
                <a href={v.sourceUrl} target="_blank" rel="noreferrer noopener" title={`Revision ${v.sourceRef}`}>
                  source
                </a>
              </p>
            )
          }

          // Composed here in plain words from the same fields the server's label is built from
          // ("captured from ScubaGear main (ahead of v1.8.0)" was the pipeline's vocabulary). The
          // revision stays on the link for an auditor.
          return (
            <p key={product} className="coverage-provenance">
              Measured against the {v.name} baseline as of {plainDate(v.capturedOn)}
              {v.exceedsRelease
                ? ` — newer than CISA's last formal release (${v.exceedsRelease})`
                : ''}
              .{' '}
              <a href={v.sourceUrl} target="_blank" rel="noreferrer noopener" title={`Revision ${v.sourceRef}`}>
                Source
              </a>
            </p>
          )
        })}

      {/*
        Proportional, and labelled with counts rather than percentages. It is a picture of the
        ratio already stated above it, not a gauge: nothing here is scored, nothing is out of a
        hundred, and the key carries its own words so the meaning survives a monochrome print of a
        board pack. One segment per status rather than three lumped bands, so the three separate
        reasons we cannot check something stay three separate reasons.
      */}
      <div className="coverage-bar" role="presentation">
        {ledger.map((group) => (
          <span
            key={group.status}
            className={`coverage-bar-seg ${group.tone}`}
            style={{ flexGrow: group.count }}
          />
        ))}
      </div>

      <ul className="coverage-key">
        {ledger.map((group) => (
          <li key={group.status}>
            <span className={`coverage-swatch ${group.tone}`} />
            <span className="coverage-key-count">{group.count}</span>
            <span className="coverage-key-what">
              {REASON[group.status]?.legend ?? `carry a status this page does not recognise: ${group.status}`}
            </span>
          </li>
        ))}
      </ul>

      {/*
        The design links out to a page listing all thirty-four. There is no such page, so this
        opens them in place rather than pointing at a route that does not exist -- and the full
        list is what backs up the claim in the key above it, so it stays on the page that makes
        the claim.
      */}
      <details className="coverage-all">
        <summary className="coverage-all-summary">See all {total} controls</summary>

        <div className="coverage-groups">
          {detail.map((group) => (
            <details
              key={group.status}
              className="coverage-group"
              // Open where there is something to answer for: any group holding a policy we do not
              // check, and any status this build does not recognise. The rest fold, because a
              // reader looking for the gap should not have to scroll past twenty-three rows of
              // things that went fine to reach it.
              open={!group.known || group.policies.some((p) => !p.hasCheck)}
            >
              <summary className="coverage-group-head">
                <span className="coverage-group-name">
                  {REASON[group.status]?.heading ?? `Status not recognised: ${group.status}`}
                </span>
                <span className="coverage-group-count">{group.count}</span>
              </summary>

              <p className="coverage-group-note">
                {REASON[group.status]?.note ??
                  'These policies carry a status this page does not know how to describe. That is a ' +
                    'fault on our side; the policies themselves are listed so nothing is hidden by it.'}
              </p>

              <ul className="coverage-policies">
                {group.policies.map((policy) => (
                  <li key={policy.reference} className="coverage-policy">
                    {/* CISA's sentence, transcribed rather than paraphrased -- so a reader holding
                        the published baseline can match this line to it word for word. */}
                    <p className="coverage-policy-statement">{policy.statement}</p>

                    <div className="coverage-policy-head">
                      <span className="coverage-policy-crit">{criticalityLabel(policy.criticality)}</span>
                      <span className="coverage-policy-ref">{policy.reference}</span>
                    </div>

                    {/* The reason, in plain words -- a gap named without a reason is a disclaimer.
                        Never the technical text from the database (see PLAIN_DETAIL). */}
                    {PLAIN_DETAIL[policy.reference] ? (
                      <p className="coverage-policy-detail">{PLAIN_DETAIL[policy.reference]}</p>
                    ) : null}
                  </li>
                ))}
              </ul>
            </details>
          ))}
        </div>
      </details>

      {/*
        The check INVENTORY, kept strictly apart from the SCuBA ratio above and below the "see all"
        list so it can never be read as part of the denominator. It answers "what else does this build
        do", which the SCuBA ledger deliberately does not — but it is an inventory of what the build can
        ATTEMPT, not a coverage claim: no count here says a check ran on this tenant or what it found.
        The copy is guarded by a test that forbids "covered", "passed", "assessed" and percentages in
        this block, because those are exactly the words that would turn an honest inventory back into
        the overstatement the whole card exists to avoid.
      */}
      {checkInventory ? (
        <div className="coverage-inventory">
          {/* PLAIN WORDS (owner, 2026-09-25). The facts are the same -- counts, what is outside the
              count above, and that this is a list of what we look for rather than a result --
              without the internal keys, product codes and "ratio/ledger" vocabulary. */}
          <h3 className="coverage-inventory-head">Other checks we run</h3>

          {checkInventory.scubaMappedOutsideLedger.total > 0 ? (
            <p className="coverage-inventory-note">
              <strong>{checkInventory.scubaMappedOutsideLedger.total} more CISA controls</strong> for{' '}
              {listPhrase(Object.keys(checkInventory.scubaMappedOutsideLedger.byProduct).map(productName))}.
              They are not in the count above, which covers Entra&nbsp;ID only.
            </p>
          ) : null}

          {checkInventory.additional.total > 0 ? (
            <>
              <p className="coverage-inventory-note">
                <strong>{checkInventory.additional.total} checks beyond CISA&apos;s baseline</strong>,
                on{' '}
                {listPhrase(
                  Object.keys(checkInventory.additional.byDomain).map(
                    (d) => AREA_PHRASE[d] ?? d.toLowerCase()),
                )}
                .
              </p>
              <details className="coverage-inventory-list">
                <summary className="coverage-inventory-summary">
                  See these {checkInventory.additional.total} checks
                </summary>
                <ul className="coverage-inventory-checks">
                  {checkInventory.additional.checks.map((c) => (
                    <li key={c.key} className="coverage-inventory-check">
                      {c.title}
                    </li>
                  ))}
                </ul>
              </details>
            </>
          ) : null}

          <p className="coverage-inventory-caveat">
            This is what we look for, not a score. It does not mean each check ran on your tenant —
            that depends on your licences and the access you granted — and each is tried on every
            assessment. What they find is in your findings.
          </p>

          {checkInventory.mappedToUnknownPolicy.length > 0 ? (
            <p className="coverage-provenance coverage-provenance-warn">
              {checkInventory.mappedToUnknownPolicy.length}{' '}
              {checkInventory.mappedToUnknownPolicy.length === 1
                ? 'check maps'
                : 'checks map'}{' '}
              to a CISA SCuBA policy that is not in the published baseline listed above, even though that product
              is — a mapping we cannot back. That is a fault on our side:{' '}
              {checkInventory.mappedToUnknownPolicy.map((c) => c.scubaReference ?? c.key).join(', ')}.
            </p>
          ) : null}

          {checkInventory.uncatalogued.length > 0 ? (
            <p className="coverage-provenance coverage-provenance-warn">
              {checkInventory.uncatalogued.length}{' '}
              {checkInventory.uncatalogued.length === 1
                ? 'check has'
                : 'checks have'}{' '}
              no description on file, so they cannot be described here. That is a fault on our side:{' '}
              {checkInventory.uncatalogued.join(', ')}.
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}
